import { Box, TextField, Typography } from "@mui/material";
import { useState } from "react";
import useIsMobile from "components/hooks/useIsMobile";
import { StepHeader } from "../components/StepHeader";
import { NextStepButton } from "../components/NextStepButton";
import FlexBox from "components/common/FlexBox";

export interface Props {
  title: string;
  subtitle: string;
  bodyContent?: React.ReactNode;
  placeholder?: string;
  skippable?: boolean;
  onNext?: (inputText: string) => Promise<void> | undefined;
  initialText?: string | undefined;
  buttonText?: string;
  label?: string;
  multiline?: boolean;
  forcePrimaryButton?: boolean;
}

export default ({
  title,
  subtitle,
  bodyContent,
  placeholder,
  skippable = false,
  onNext,
  initialText,
  buttonText,
  label,
  multiline = true,
  forcePrimaryButton = false,
}: Props) => {
  const isMobile = useIsMobile();
  const [inputValue, setInputValue] = useState(initialText || "");
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    setLoading(true);
    onNext && (await onNext(inputValue));
    setLoading(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        padding: isMobile ? "" : "0 32px",
        gap: isMobile ? "32px" : "64px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          width: isMobile ? "100%" : "70%",
          maxWidth: "740px",
          gap: "12px",
        }}
      >
        <StepHeader title={title} subtitle={subtitle} />

        {/* Body context */}
        {bodyContent}

        {/* Open input */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            alignItems: "end",
          }}
        >
          <FlexBox direction="column" sx={{ width: "100%" }}>
            {label && <Typography variant="h4">{label}</Typography>}
            <TextField
              multiline={multiline}
              placeholder={placeholder}
              variant="outlined"
              value={inputValue}
              fullWidth
              onChange={(e) => setInputValue(e.target.value)}
              sx={{
                "& .MuiInputBase-root": {
                  color: "inherit",
                  fontWeight: "500",
                  minHeight: multiline ? "150px" : "60px",
                  alignItems: "start",
                  "& textarea": {
                    marginTop: 0,
                    fontSize: "16px",
                  },
                  "& .MuiInputBase-input": {
                    padding: multiline ? "20px 24px" : "5px 24px",
                  },
                },
              }}
            />
          </FlexBox>

          <NextStepButton
            skippable={skippable}
            hasValue={inputValue !== ""}
            continueClick={handleContinue}
            disabled={loading}
            buttonText={buttonText}
            forcePrimary={forcePrimaryButton}
            minWidth={isMobile ? "100%" : undefined}
          />
        </Box>
      </Box>
    </Box>
  );
};
